import React, { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AddCircleOutlineRounded as NewVersionIcon,
  ContentCopy as CopyIcon,
  MoreHoriz as MoreHorizIcon,
  WarningAmberRounded as WarningIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  useTheme,
} from '@mui/material';
import { observer } from 'mobx-react-lite';

import { routes } from '@app/routes';
import {
  useDeleteAssessment,
  useDownloadAllDocumentsZip,
  useDuplicateAssessment,
  useDuplicateTitleField,
  useGetReportDownloadUrl,
  useRetryAssessment,
} from '@features/assessment';
import { AssessmentStatus } from '@libs/constants';
import { Trans } from '@libs/i18n';
import { Assessment } from '@libs/types';

import { DuplicateTitleField } from '../shared/DuplicateTitleField';

import { canStartNewVersion, StartNewVersionConfirmDialog } from './StartNewVersionConfirmDialog';

interface AssessmentActionsProps {
  assessment: Assessment;
  hideDelete?: boolean;
  hideDuplicate?: boolean;
}

export const AssessmentActions: FC<AssessmentActionsProps> = observer(({ assessment, hideDelete = false, hideDuplicate = false }) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [retryDialogOpen, setRetryDialogOpen] = useState(false);
  const [failureReasonDialogOpen, setFailureReasonDialogOpen] = useState(false);
  const [newVersionDialogOpen, setNewVersionDialogOpen] = useState(false);
  const navigate = useNavigate();
  const { mutate: deleteAssessment, isPending: isDeleting } = useDeleteAssessment();
  const { mutate: duplicateAssessment, isPending: isDuplicating } = useDuplicateAssessment();
  const { mutate: retryAssessment, isPending: isRetrying } = useRetryAssessment();
  const reportDownloadMutation = useGetReportDownloadUrl();
  const documentsZipMutation = useDownloadAllDocumentsZip();
  const duplicateTitle = useDuplicateTitleField(assessment.title, duplicateDialogOpen);

  const showStartNewVersion =
    assessment.status === AssessmentStatus.COMPLETED &&
    canStartNewVersion(assessment, AssessmentStatus.DRAFT);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleResume = () => {
    const { currentDomainIndex } = assessment;

    if (currentDomainIndex !== null && currentDomainIndex !== undefined) {
      navigate(routes.assessmentDomain(assessment.id, currentDomainIndex));
    }

    handleClose();
  };

  const handleDeleteClick = () => {
    handleClose();
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    deleteAssessment(
      { assessmentId: assessment.id },
      {
        onSuccess: () => {
          setDeleteDialogOpen(false);
        },
        onError: (error: unknown) => {
          console.error('Failed to delete assessment:', error);
          // Keep dialog open to show error or close it
          setDeleteDialogOpen(false);
        },
      },
    );
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleDuplicateClick = () => {
    handleClose();
    setDuplicateDialogOpen(true);
  };

  const handleDuplicateConfirm = () => {
    if (!duplicateTitle.isValid || isDuplicating) return;

    duplicateAssessment(
      { assessmentId: assessment.id, title: duplicateTitle.trimmedTitle },
      // Only on success: a failed duplicate leaves the dialog open with the
      // title the user typed, so retrying doesn't mean typing it again.
      { onSuccess: () => setDuplicateDialogOpen(false) },
    );
  };

  const handleDownloadReport = () => {
    reportDownloadMutation.mutate({ assessmentId: assessment.id });
    handleClose();
  };

  const handleDownloadAllDocuments = () => {
    documentsZipMutation.mutate({ assessmentId: assessment.id });
    handleClose();
  };

  const handleViewSummary = () => {
    navigate(routes.assessmentOverview(assessment.id));
    handleClose();
  };

  const handleRetryClick = () => {
    handleClose();
    setRetryDialogOpen(true);
  };

  const handleRetryConfirm = () => {
    retryAssessment(
      { assessmentId: assessment.id },
      { onSettled: () => setRetryDialogOpen(false) },
    );
  };

  const handleViewFailureReason = () => {
    handleClose();
    setFailureReasonDialogOpen(true);
  };

  const handleStartNewVersionClick = () => {
    handleClose();
    setNewVersionDialogOpen(true);
  };

  return (
    <>
      {(assessment.status !== AssessmentStatus.PROCESSING && assessment.status !== AssessmentStatus.SUBMITTED) && (
        <>
          <IconButton
            size="small"
            onClick={handleClick}
            sx={{
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: theme.palette.action.hover,

              },
            }}
          >
            <MoreHorizIcon fontSize="small" />
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            PaperProps={{
              sx: {
                minWidth: 160,
                borderRadius: 2,
                border: 'none',
                boxShadow: theme.shadows[8],
              },
            }}
          >
            {assessment.status === AssessmentStatus.DRAFT && (
              <Box>
                <MenuItem onClick={handleResume}>
                  <Trans i18nKey="app.assessments.actions.resume" />
                </MenuItem>
                {!hideDuplicate && (
                  <MenuItem onClick={handleDuplicateClick}>
                    <Trans i18nKey="app.assessments.actions.duplicate" />
                  </MenuItem>
                )}
                {!hideDelete && (
                  <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
                    <Trans i18nKey="app.assessments.actions.delete" />
                  </MenuItem>
                )}
              </Box>
            )}

            {assessment.status === AssessmentStatus.COMPLETED && (
              <Box>
                <MenuItem onClick={handleViewSummary}>
                  <Trans i18nKey="app.assessments.actions.viewSummary" />
                </MenuItem>
                <MenuItem onClick={handleDownloadReport}>
                  <Trans i18nKey="app.assessments.actions.downloadReport" />
                </MenuItem>
                <MenuItem onClick={handleDownloadAllDocuments}>
                  <Trans i18nKey="app.assessment.document.downloadAll" />
                </MenuItem>
                {showStartNewVersion && (
                  <MenuItem onClick={handleStartNewVersionClick}>
                    <NewVersionIcon
                      fontSize="small"
                      sx={{ marginInlineEnd: 1, color: theme.palette.primary.main }}
                    />
                    <Trans i18nKey="app.assessments.actions.startNewVersion" />
                  </MenuItem>
                )}
                {!hideDuplicate && (
                  <MenuItem onClick={handleDuplicateClick}>
                    <Trans i18nKey="app.assessments.actions.duplicate" />
                  </MenuItem>
                )}
                {!hideDelete && (
                  <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
                    <Trans i18nKey="app.assessments.actions.delete" />
                  </MenuItem>
                )}
              </Box>
            )}

            {assessment.status === AssessmentStatus.FAILED && (
              <Box>
                <MenuItem onClick={handleRetryClick}>
                  <Trans i18nKey="app.assessments.actions.retry" />
                </MenuItem>
                <MenuItem onClick={handleViewFailureReason}>
                  <Trans i18nKey="app.assessments.actions.viewFailureReason" />
                </MenuItem>
                {!hideDelete && (
                  <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
                    <Trans i18nKey="app.assessments.actions.delete" />
                  </MenuItem>
                )}
              </Box>
            )}
          </Menu>
        </>
      )}

      {/* Retry Confirmation Dialog */}
      <Dialog
        open={retryDialogOpen}
        onClose={() => !isRetrying && setRetryDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 3, minWidth: 400, maxWidth: 500 } }}
      >
        <DialogTitle sx={{ pb: 2 }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                borderRadius: '50%',
                backgroundColor: 'warning.lighter',
                color: 'warning.main',
              }}
            >
              <WarningIcon sx={{ fontSize: 24 }} />
            </Box>
            <Typography variant="h6" component="span" fontWeight={600}>
              <Trans i18nKey="app.assessments.retry.title" />
            </Typography>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <DialogContentText>
            <Trans i18nKey="app.assessments.retry.confirmMessage" />
          </DialogContentText>
          <Box
            sx={{
              mt: 2,
              p: 2,
              borderRadius: 2,
              backgroundColor: 'action.hover',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500 }}>
              <Trans i18nKey="app.assessments.delete.assessmentTitle" />
            </Typography>
            <Typography variant="body1" fontWeight={600} color="text.primary">
              {assessment.title}
            </Typography>
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            onClick={() => setRetryDialogOpen(false)}
            disabled={isRetrying}
            variant="outlined"
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3 }}
          >
            <Trans i18nKey="app.common.cancel" />
          </Button>
          <Button
            onClick={handleRetryConfirm}
            variant="contained"
            color="warning"
            disabled={isRetrying}
            startIcon={isRetrying ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3 }}
          >
            {isRetrying
              ? <Trans i18nKey="app.assessments.retry.retrying" />
              : <Trans i18nKey="app.assessments.retry.confirm" />
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Failure Reason Dialog */}
      <Dialog
        open={failureReasonDialogOpen}
        onClose={() => setFailureReasonDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 3, minWidth: 400, maxWidth: 560 } }}
      >
        <DialogTitle sx={{ pb: 2 }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                borderRadius: '50%',
                backgroundColor: 'error.lighter',
                color: 'error.main',
              }}
            >
              <WarningIcon sx={{ fontSize: 24 }} />
            </Box>
            <Typography variant="h6" component="span" fontWeight={600}>
              <Trans i18nKey="app.assessments.failureReason.title" />
            </Typography>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          {assessment.failureReason ? (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              <Typography variant="body2">{assessment.failureReason}</Typography>
            </Alert>
          ) : (
            <Typography variant="body2" color="text.secondary">
              <Trans i18nKey="app.assessments.failureReason.noReason" />
            </Typography>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setFailureReasonDialogOpen(false)}
            variant="contained"
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3 }}
          >
            <Trans i18nKey="app.common.close" />
          </Button>
        </DialogActions>
      </Dialog>

      <StartNewVersionConfirmDialog
        assessment={assessment}
        open={newVersionDialogOpen}
        onClose={() => setNewVersionDialogOpen(false)}
      />

      {/* Duplicate Confirmation Dialog */}
      <Dialog
        open={duplicateDialogOpen}
        onClose={() => !isDuplicating && setDuplicateDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 3, minWidth: 400, maxWidth: 500 } }}
      >
        <DialogTitle sx={{ pb: 2 }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                borderRadius: '50%',
                backgroundColor: 'primary.lighter',
                color: 'primary.main',
              }}
            >
              <CopyIcon sx={{ fontSize: 22 }} />
            </Box>
            <Typography variant="h6" component="span" fontWeight={600}>
              <Trans i18nKey="app.assessments.duplicate.title" />
            </Typography>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <DialogContentText sx={{ mb: 2 }}>
            <Trans i18nKey="app.assessments.duplicate.confirmMessage" />
          </DialogContentText>
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: 2,
              backgroundColor: 'action.hover',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.25, fontWeight: 500 }}>
              <Trans i18nKey="app.assessments.duplicate.sourceLabel" />
            </Typography>
            <Typography variant="body2" fontWeight={600} color="text.primary">
              {assessment.title}
            </Typography>
          </Box>
          <DuplicateTitleField
            field={duplicateTitle}
            onSubmit={handleDuplicateConfirm}
            disabled={isDuplicating}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
          />
          <Box
            sx={{
              mt: 1,
              p: 1.5,
              borderRadius: 2,
              backgroundColor: 'info.lighter',
              display: 'flex',
              gap: 1,
              alignItems: 'flex-start',
            }}
          >
            <CopyIcon sx={{ fontSize: 18, color: 'info.main', mt: 0.25, flexShrink: 0 }} />
            <Typography variant="body2" color="info.dark" fontWeight={500}>
              <Trans i18nKey="app.assessments.duplicate.info" />
            </Typography>
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            onClick={() => setDuplicateDialogOpen(false)}
            disabled={isDuplicating}
            variant="outlined"
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3 }}
          >
            <Trans i18nKey="app.common.cancel" />
          </Button>
          <Button
            onClick={handleDuplicateConfirm}
            variant="contained"
            color="primary"
            disabled={isDuplicating || !duplicateTitle.isValid}
            startIcon={isDuplicating ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3 }}
          >
            {isDuplicating
              ? <Trans i18nKey="app.assessments.duplicate.duplicating" />
              : <Trans i18nKey="app.assessments.duplicate.confirm" />
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: 3,
            minWidth: 400,
            maxWidth: 500,
          },
        }}
      >
        <DialogTitle id="delete-dialog-title" sx={{ pb: 2 }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                borderRadius: '50%',
                backgroundColor: 'error.lighter',
                color: 'error.main',
              }}
            >
              <WarningIcon sx={{ fontSize: 24 }} />
            </Box>
            <Typography variant="h6" component="span" fontWeight={600}>
              <Trans i18nKey="app.assessments.delete.title" />
            </Typography>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <DialogContentText id="delete-dialog-description" sx={{ mb: 2 }}>
            <Trans i18nKey="app.assessments.delete.confirmMessage" />
          </DialogContentText>
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              backgroundColor: 'action.hover',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500 }}>
              <Trans i18nKey="app.assessments.delete.assessmentTitle" />
            </Typography>
            <Typography variant="body1" fontWeight={600} color="text.primary">
              {assessment.title}
            </Typography>
          </Box>
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: 2,
              backgroundColor: 'error.lighter',
              display: 'flex',
              gap: 1,
              alignItems: 'flex-start',
            }}
          >
            <WarningIcon sx={{ fontSize: 20, color: 'error.main', mt: 0.25 }} />
            <Typography variant="body2" color="error.dark" fontWeight={500}>
              <Trans i18nKey="app.assessments.delete.warning" />
            </Typography>
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            onClick={handleDeleteCancel}
            disabled={isDeleting}
            variant="outlined"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
            }}
          >
            <Trans i18nKey="app.common.cancel" />
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              backgroundColor: 'error.main',
              color: theme.palette.background.paper,
              boxShadow: 2,
              '&:hover': {
                backgroundColor: 'error.dark',
                boxShadow: 3,
              },
              '&:disabled': {
                backgroundColor: 'error.light',
                color: theme.palette.background.paper,
                opacity: 0.7,
              },
            }}
          >
            {isDeleting ? (
              <Trans i18nKey="app.assessments.actions.deleting" />
            ) : (
              <Trans i18nKey="app.assessments.actions.deleteAssessment" />
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});
